import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import get from 'lodash/get';

// MaterialUI
import Button from '@material-ui/core/Button';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import Tooltip from '@material-ui/core/Tooltip';

// app: components
import ConfirmDeletionDialog from 'components/ConfirmDeletionDialog';

// app: queries
import { DELETE_COMMENT } from '../CommentQueries';

const CommentEditActionsMenu = props => {
  const { contentId, commentId, userId, type, onDeleteError, onDeleteCompleted } = props;
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);

  const [deleteComment] = useMutation(DELETE_COMMENT, {
    onCompleted: () => {
      setConfirmDeleteDialogOpen(false);
      onDeleteCompleted();
    },
    onError: err => {
      console.error('Failed to delete comment', err);
      const errorCode = get(err, 'graphQLErrors[0].extensions.code');
      const niceErrorCode = errorCode ? ` (code: ${errorCode})` : '';
      const niceSaveError =
        get(err, 'graphQLErrors[0].message') ||
        `We can't save this right now. Please try again later${niceErrorCode}`;
      setConfirmDeleteDialogOpen(false);
      onDeleteError(niceSaveError);
    },
    update(
      cache,
      {
        data: { DeleteUserComment },
      },
    ) {
      if (DeleteUserComment && DeleteUserComment.deleted && DeleteUserComment.id) {
        const id = `Comment:${DeleteUserComment.id}`;
        cache.evict({ id });

        // Update the comment counters
        if (type) {
          cache.modify({
            id: `CommentCounts:${contentId}`,
            fields: {
              [type](cachedCount) {
                return cachedCount > 0 ? cachedCount - 1 : null;
              },
            },
          });
        }
      }
    },
  });

  const handleDelete = event => {
    event.stopPropagation();
    setConfirmDeleteDialogOpen(true);
  };

  const handleCancelDelete = event => {
    event.stopPropagation();
    setConfirmDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async event => {
    event.stopPropagation();

    await deleteComment({
      variables: { comment_id: commentId, user_id: userId, content_id: contentId, type },
    });
  };

  return (
    <div>
      <Tooltip title={`Delete ${type}`} aria-label="delete">
        <Button size="small" color="primary" onClick={handleDelete}>
          <DeleteIcon />
        </Button>
      </Tooltip>
      <ConfirmDeletionDialog
        title={`Are you sure you want to delete this ${type}?`}
        body=""
        open={confirmDeleteDialogOpen}
        onClose={handleCancelDelete}
        onAgree={handleConfirmDelete}
      />
    </div>
  );
};
CommentEditActionsMenu.propTypes = {
  commentId: PropTypes.number.isRequired,
  // Not required, but if provided it allows the comment counts to be updated
  userId: PropTypes.number,
  contentId: PropTypes.string,
  type: PropTypes.string,
  onDeleteError: PropTypes.func,
  onDeleteCompleted: PropTypes.func,
};

CommentEditActionsMenu.defaultProps = {
  contentId: null,
  userId: null,
  type: 'comment',
  onDeleteError: () => {},
  onDeleteCompleted: () => {},
};

export default React.memo(CommentEditActionsMenu);
